import { AppApiGatewayService, fetchEventMedia, fetchEventVideo } from 'src/common/services';
import { VQAResponse } from 'src/common/types';
import { Event, ChatMessage, ChatMessageType, NotificationSource } from '../types';

/**
 * Fetch thumbnail and video for an event.
 *
 * 1. Calls fetchEventMedia (fast EVM call) to get thumbnail + video URL + device ID.
 * 2. If the video URL is directly playable, uses it as-is.
 * 3. If not playable, falls back to fetchEventVideo which transcodes via Phoenix
 *    and returns a guaranteed-playable blob URL.
 */
export async function fetchVideoForEvent(
  service: AppApiGatewayService,
  eventId: string,
): Promise<Event> {
  try {
    const media = await fetchEventMedia(service, eventId);
    let videoUrl = media.videoUrl ?? '';
    let isBlobUrl = false;

    if (!videoUrl && media.deviceId && media.durationMs > 0) {
      // No playable video URL from EVM — transcode via Phoenix as blob
      videoUrl = await fetchEventVideo(service, media.deviceId, media.startTimestampMs, media.durationMs);
      isBlobUrl = true;
    }

    if (!videoUrl) {
      return { id: eventId, thumbnailUrl: media.thumbnailUrl ?? '', videoUrl: '', hasError: true };
    }

    return {
      id: eventId,
      thumbnailUrl: media.thumbnailUrl ?? '',
      videoUrl,
      timestamp: media.startTimestampMs ? new Date(media.startTimestampMs) : undefined,
      deviceId: media.deviceId,
      startTimestampMs: media.startTimestampMs,
      durationMs: media.durationMs,
      isBlobUrl,
    };
  } catch (error) {
    console.error(`Failed to fetch media for event ${eventId}:`, error);
    return { id: eventId, thumbnailUrl: '', videoUrl: '', hasError: true };
  }
}

export class VideoHandler {
  protected backendService: AppApiGatewayService;
  protected setConversations: React.Dispatch<React.SetStateAction<ChatMessage[]>>;

  constructor(
    backendService: AppApiGatewayService,
    setConversations: React.Dispatch<React.SetStateAction<ChatMessage[]>>
  ) {
    this.backendService = backendService;
    this.setConversations = setConversations;
  }

  protected updateConversationWithEvents(vqaResponse: VQAResponse, events: Event[], onComplete?: () => void) {
    this.setConversations(prev => {
      const updated = [...prev];
      const messageIndex = prev.findIndex(m => m.timestamp === vqaResponse.prompt_creation_time);
      if (messageIndex === -1 || prev[messageIndex].type !== ChatMessageType.BOT) {
        return prev;
      }

      const updatedMessage: ChatMessage = {
        ...updated[messageIndex],
        content: vqaResponse.text || "",
        events,
        isStreaming: false,
        reasoning: "",
        error: undefined
      };

      updated[messageIndex] = updatedMessage;
      onComplete?.();
      return updated;
    });
  }

  protected getEventIdFromSource(notificationMessageMap: React.MutableRefObject<Map<string, {eventId: string, source: NotificationSource}>>, messageId: string): string | null {
    // Get eventId from the messageId -> {eventId, source} mapping
    const notificationData = notificationMessageMap.current.get(messageId);
    console.log('🗺️ EventId from map for messageId', messageId, ':', notificationData);

    if (notificationData) {
      return notificationData.eventId;
    } else {
      console.error('🚨 MessageId not found in notification map:', messageId);
      return null;
    }
  }
  protected handleVideoFetchError(vqaResponse: VQAResponse, error: any, onComplete?: () => void) {
    console.error('Error fetching video data:', error);
    this.setConversations(prev => {
      const updated = [...prev];
      const messageIndex = prev.findIndex(m => m.timestamp === vqaResponse.prompt_creation_time);
      if (messageIndex === -1 || prev[messageIndex].type !== ChatMessageType.BOT) {
        return prev;
      }

      const errorMessage: ChatMessage = {
        ...updated[messageIndex],
        content: "",
        reasoning: "",
        events: undefined,
        isStreaming: false,
        error: "Sorry, I encountered an error while fetching video data. Please try again."
      };

      updated[messageIndex] = errorMessage;
      onComplete?.();
      return updated;
    });
  }

  protected async processEvents(response: VQAResponse, eventIds: string[], onComplete?: () => void) {
    if (!eventIds.length) return;

    try {
      const events = await Promise.all(eventIds.map(id => fetchVideoForEvent(this.backendService, id)));
      this.updateConversationWithEvents(response, events, onComplete);
    } catch (error) {
      this.handleVideoFetchError(response, error, onComplete);
    }
  }
}

export class NotificationVideoHandler extends VideoHandler {
  async handle(response: VQAResponse, locationState: any, source: NotificationSource, notificationMessageMap: React.MutableRefObject<Map<string, {eventId: string, source: NotificationSource}>>, onComplete?: () => void) {
    // TODO: If user clicks the other notifications before the thumbnail pop-up, then it will override the 1st notification video
    console.log('🔍 NotificationVideoHandler - messageId:', response.messageId);

    const eventId = this.getEventIdFromSource(notificationMessageMap, response.messageId);
    if (!eventId) return;

    await this.processEvents(response, [eventId], onComplete);
  }
}

export class VQAVideoHandler extends VideoHandler {
  async handle(response: VQAResponse) {
    if (!response.events?.length) return;

    const visibleEvents = response.events.slice(0, 6);
    const remainingEvents = response.events.slice(6);

    // Create immediate placeholders for visible events with loading states
    const visibleEventIds = visibleEvents.filter(e => e.eventId).map(e => e.eventId);
    const loadingEvents: Event[] = visibleEventIds.map(id => ({
      id,
      thumbnailUrl: '',
      videoUrl: '',
      isLoading: true
    }));

    // Create placeholders for remaining events — they'll be fetched on demand
    const placeholderEvents: Event[] = remainingEvents
      .filter(e => e.eventId)
      .map(e => ({ id: e.eventId, thumbnailUrl: '', videoUrl: '', needsFetch: true }));

    // Show the grid immediately with loading placeholders
    this.updateConversationWithEvents(response, [...loadingEvents, ...placeholderEvents]);

    // Fetch visible videos individually and update as they complete
    this.fetchVideosProgressively(response, visibleEventIds, placeholderEvents);
  }

  private async fetchVideosProgressively(response: VQAResponse, eventIds: string[], placeholderEvents: Event[]) {
    // Fetch each video individually and update the UI as they complete
    const fetchPromises = eventIds.map(async (eventId, index) => {
      try {
        const fetchedEvent = await fetchVideoForEvent(this.backendService, eventId);
        
        // fetchVideoForEvent catches internally and sets hasError — propagate it
        const completeEvent = {
          ...fetchedEvent,
          isLoading: false
        };
        
        // Update this specific video in the conversation
        this.updateSingleVideoInConversation(response, completeEvent, index);
      } catch (error) {
        console.error(`Failed to fetch video for event ${eventId}:`, error);
        
        // Update with error state
        const errorEvent: Event = {
          id: eventId,
          thumbnailUrl: '',
          videoUrl: '',
          hasError: true,
          isLoading: false
        };
        this.updateSingleVideoInConversation(response, errorEvent, index);
      }
    });

    // Don't await - let them complete independently
    fetchPromises.forEach(p => p.catch(() => {}));
  }

  private updateSingleVideoInConversation(response: VQAResponse, updatedEvent: Event, index: number) {
    this.setConversations(prev => {
      const updated = [...prev];
      const messageIndex = prev.findIndex(m => m.timestamp === response.prompt_creation_time);
      
      if (messageIndex === -1 || prev[messageIndex].type !== ChatMessageType.BOT) {
        return prev;
      }

      const currentMessage = updated[messageIndex];
      if (!currentMessage.events) {
        return prev;
      }

      // Update the specific video at the given index
      const updatedEvents = [...currentMessage.events];
      updatedEvents[index] = updatedEvent;

      const updatedMessage: ChatMessage = {
        ...currentMessage,
        events: updatedEvents
      };

      updated[messageIndex] = updatedMessage;
      return updated;
    });
  }
}
